import { FC, Fragment, useMemo } from 'react';

import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import { FolderEmpty } from '@assets/icons';
import { AssetType } from '@pages/home-page/enums';
import { DataItem } from '@pages/home-page/types';
import { hexToRgbaString } from '@utils/color';

import DraggableAssetRow from '../DraggableAssetRow';

interface DraggableAttributeSectionProps {
  workspaceId: number;
  knowledgeCategory: string;
  attributeItems: DataItem[];
  handleDelete: (id: number, assetType: AssetType) => void;
  handleEdit?: (item: DataItem, assetType: AssetType) => void;
  isDraggable?: boolean;
  handleShowMetadata: (item: DataItem) => void;
}

const groupAttributeItems = (
  items: DataItem[],
  workspaceId: number,
): (DataItem | DataItem[])[] => {
  const groupedItems: (DataItem | DataItem[])[] = [];
  let tempGroup: DataItem[] = [];

  for (const item of items) {
    if (item.parentClientId === workspaceId) {
      if (tempGroup.length) {
        groupedItems.push(tempGroup);
        tempGroup = [];
      }
      groupedItems.push(item);
    } else {
      tempGroup.push(item);
    }
  }

  if (tempGroup.length) groupedItems.push(tempGroup);

  return groupedItems;
};

const DraggableAttributeSection: FC<DraggableAttributeSectionProps> = ({
  workspaceId,
  knowledgeCategory,
  attributeItems,
  handleDelete,
  handleEdit,
  isDraggable = true,
  handleShowMetadata,
}) => {
  const primaryColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--primary-color')
    .trim();
  const transparentColor = hexToRgbaString(primaryColor, 0);

  const combinedAttributeItems = useMemo(
    () => groupAttributeItems(attributeItems, workspaceId),
    [attributeItems, workspaceId],
  );

  return (
    <SortableContext
      items={attributeItems}
      strategy={verticalListSortingStrategy}
    >
      <Fragment key={knowledgeCategory}>
        <div
          className="my-4 ml-4 py-2 px-3 uppercase text-sm w-fit font-semibold tracking-widest"
          style={{
            borderWidth: '2px 2px 0px 2px',
            borderImageSource: `linear-gradient(270deg, ${transparentColor} -8.32%,  var(--primary-color) 56.68%, ${transparentColor} 121.88%)`,
            borderImageSlice: 1,
            borderRadius: '20px',
          }}
        >
          {knowledgeCategory}
        </div>
        {combinedAttributeItems?.length ? (
          combinedAttributeItems?.map(item => {
            const isItemArray = Array.isArray(item);

            return (
              <DraggableAssetRow
                key={isItemArray ? `group-${item[0].id}` : item.id}
                item={item}
                handleDelete={handleDelete}
                handleEdit={handleEdit}
                isDraggable={isDraggable}
                workspaceId={workspaceId}
                isGrouped={isItemArray}
                isDocumentRow={false}
                handleShowMetadata={handleShowMetadata}
              />
            );
          })
        ) : (
          <tr>
            <td colSpan={4} className="text-center py-8">
              <div className="flex flex-col items-center justify-center gap-1">
                <FolderEmpty />
                <p className="text-sm text-dark-3 font-semibold">File Empty</p>
                <p className="text-sm text-dark-3">
                  {`Seems like no ${knowledgeCategory} has been added to the library`}
                </p>
              </div>
            </td>
          </tr>
        )}
      </Fragment>
    </SortableContext>
  );
};

export default DraggableAttributeSection;
